<script>
    import RecommendationModel from "./Models/recommendationModel";
    import {writable} from 'svelte/store';
    import Loader from './Components/Loader.svelte';
    import {addFormModal} from './Forms/ModalFormManager.svelte';
    import {forms} from './lib/forms';
    import {notifier} from '@beyonk/svelte-notifications';
    import {confirm} from './Components/Confirmation.svelte';
    import SortableTable from './Components/SortableTable.svelte';
    import {tables} from './lib/tables';

    import parseCSV from './lib/csv';

    let recommendationsList = RecommendationModel.getStockRecommendationList();

    let importing;
    let sortColumn = 'function';
    let sortAscending = true;
    let filters = writable({});


    function addRecommendation() {
        addFormModal({
            fields: forms['recommendation']
        }).then(formData => {
            RecommendationModel.createStockRecommendation(formData).then(() => notifier.success("Recommendation Added"));
        }).catch(() => null);
    }

    function editRecommendation(recommendation) {
        addFormModal({
            fields: forms['recommendation'],
            data: recommendation
        }).then(formData => {
            RecommendationModel.saveStockRecommendation(formData).then(() => notifier.success("Recommendation Saved"));
        }).catch(() => null)
    }

    function deleteRecommendation(recommendation) {
        confirm("Are you sure you want to delete this recommendation?").then(() => {
            RecommendationModel.deleteStockRecommendation(recommendation).then(() => {
                notifier.success("Recommendation Deleted");
            });
        }).catch(() => null)
    }

    function importRecommendations() {
        addFormModal({
            fields: [{
                type: "file",
                name: "import",
                label: "Import File"
            }],
            title: "Import Recommendations"
        }).then(formData => {
            importing = true;

            formData.import.item(0).text().then((csv) => {
                let recommendations = parseCSV(csv);
                if (JSON.stringify(recommendations[0]) != JSON.stringify(["id", "Function","Category","Finding","Probability","Impact","Risk","Recommendation", "NIST", "CIS", "SEC", "HIPAA", "ISO", "Other"])) {
                    alert("Invalid Template");
                    importing = false;
                    return;
                }

                let imports = [];
                for (let i = 1; i < recommendations.length; i++) {
                    let row = recommendations[i]
                    if (!row[1]) continue;

                    // handle references
                    let references = []
                    let frameworks = ["NIST CSF", "CIS CSC", "SEC", "HIPAA", "ISO", "Other"]

                    for (let j = 0; j < 6; j ++) {
                        for (let reference of row[8 + j].split(',')) {
                            if (reference.trim() != "") {
                                references.push({
                                    framework: frameworks[j],
                                    control: reference.trim()
                                })
                            }
                        }
                    }


                    let recommendation = {
                        function: row[1],
                        category: row[2],
                        finding: row[3],
                        probability: row[4],
                        impact: row[5],
                        overall: row[6],
                        recommendation: row[7],
                        references: references
                    }

                    if (row[0]) {
                        imports.push(
                            RecommendationModel.getStockRecommendation(row[0]).then((result) => {
                                Object.assign(result, recommendation);
                                return RecommendationModel.saveStockRecommendation(result);
                            })
                        )
                    } else {
                        imports.push(
                            RecommendationModel.createStockRecommendation(recommendation).catch((e) => {
                                console.warning(e);
                            })
                        );
                    }
                }

                Promise.all(imports).then(() => {
                    importing = false;
                    notifier.success("Recommendations Updated");
                })
            })
        }).catch(() => null);
    }

    function sort(list, sorter, filters, reverse) {
        let copy = list.filter((item) => {
            for (let column of ["function", "category", "finding", "probability", "impact", "overall", "recommendation"]) {
                if (filters[column] && !item[column].toLowerCase().includes(filters[column].toLowerCase())) {
                    return false;
                }
            }
            return true;
        });
        copy.sort(sorter);

        if (reverse) {
            copy.reverse();
        }

        return copy
    }

    function csvEscape(string) {
        if (!string) string = "";

        if (string.includes('"') || string.includes('\n') || string.includes(',')) {
            string = `"${string.replace(/"/g, '""')}"`;
        }

        return string
    }

    function exportRecommendations() {
        let header = "id,Function,Category,Finding,Probability,Impact,Risk,Recommendation\n";
        let rows = sort($recommendationsList, sorter(sortColumn), $filters).map((row) => {
            return `${csvEscape(row._id)},${csvEscape(row.function)},${csvEscape(row.category)},${csvEscape(row.finding)},${csvEscape(row.probability)},${csvEscape(row.impact)},${csvEscape(row.overall)},${csvEscape(row.recommendation)}`
        })

        let csv_data = new Blob([header, rows.join('\n')], {type: "text/csv"});
        let link = document.createElement("a");
        link.download = "recommendations_export.csv";
        link.href = URL.createObjectURL(csv_data);
        link.click();

        URL.revokeObjectURL(link.href);

    }

    function sorter(column) {
        return (a, b) => {
            if (a[column] > b[column]) {
                return 1;
            }
            if (b[column] > a[column]) {
                return -1;
            }
            return 0;
        }
    }

    function setSortColumn(column) {
        if (sortColumn == column) {
            sortAscending = !sortAscending
        } else {
            sortColumn = column
            sortAscending = true
        }
    }

    let sortFields = tables['recommendations'];
</script>

<Loader loading={$recommendationsList == undefined}>
    <div class="container is-fluid">
        <h1 class="title is-1">Recommendations</h1>
        <SortableTable {sortFields} tableData={$recommendationsList} tableClass="NISTTable">
            <div slot="filterActions"><button class="button is-success" on:click={addRecommendation}>Add Recommendation</button></div>
            <div slot="rowActions" let:row={recommendation}>
                <div class="buttons is-centered">
                    <button class="button is-danger" on:click={() => deleteRecommendation(recommendation)}>Delete</button>
                    <button class="button is-link" on:click={() => editRecommendation(recommendation)}>Edit</button>
                </div>
            </div>
            <div slot="footerActions">
                <div class="buttons is-pulled-right">
                    <div class="button is-link" on:click={exportRecommendations}>Export Recommendations</div>
                    <div class="button is-link" on:click={importRecommendations}>Import Recommendations</div>
                </div>
            </div>
        </SortableTable>
    </div>
</Loader>

{#if importing}
    <div class="importLoader">
        <Loader></Loader>
    </div>
{/if}

<style>
    .container {
        padding-left: 32px;
        padding-right: 32px;
    }

    .importLoader {
        position: fixed;
        top: 0;
        left: 0;
        height: 100vh;
        width: 100vw;
        background-color: rgba(255,255,255, 0.75);
    }

    :global(.NISTTable td.recommendation) {
        white-space: pre-wrap;
    }

    :global(.NISTTable .function) {
        color: white;
        font-weight: bold;
    }

    :global(.NISTTable td[data-field="Identify"]) {
        background-color: #47a6dc !important;
    }

    :global(.NISTTable td[data-field="Protect"]) {
        background-color: #845295 !important;
    }

    :global(.NISTTable td[data-field="Detect"]) {
        background-color: #f99e1b !important;
    }

    :global(.NISTTable td[data-field="Respond"]) {
        background-color: #ec3945 !important;
    }

    :global(.NISTTable td[data-field="Recover"]) {
        background-color: #39b54a !important;
    }

    :global(.NISTTable td.risk) {
        font-weight: bold;
    }

    :global(.NISTTable td.risk[data-field="High"]) {
        color: red;
    }

    :global(.NISTTable td.risk[data-field="Medium"]) {
        color: orange;
    }

    :global(.NISTTable td.risk[data-field="Low"]) {
        color: green;
    }
</style>